(() => {
  // The matching cart line input is rendered as properties[design_id] in product.liquid.
  const sections = document.querySelectorAll('[data-huina-product-commerce]');

  if (!sections.length) {
    return;
  }

  const tierPattern = /(?:10-49|50-149|150-299|300-499|500\+)/u;

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/&/gu, ' and ')
      .replace(/[^a-z0-9+]+/gu, ' ')
      .trim();

  const normalizeDesignId = (value) => String(value || '').trim();

  const isValidDesignId = (value) =>
    /^(?:HUINA-DESIGN-\d{8}-[A-Z0-9]{8}|design-\d+)$/u.test(normalizeDesignId(value));

  const allowedDesignAssetHosts = new Set([
    'cdn.shopify.com',
    'customize.huinasports.com',
    'huinasports.com',
    'www.huinasports.com',
  ]);
  const allowedDesignLinkHosts = new Set([
    'customize.huinasports.com',
    'huinasports.com',
    'www.huinasports.com',
  ]);
  const allowedImageContentTypes = new Set([
    'image/avif',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/webp',
  ]);
  const allowedCsvContentTypes = new Set([
    'application/csv',
    'application/vnd.ms-excel',
    'text/csv',
  ]);
  const maxDesignUrlLength = 2048;
  const shopifyRoute = (path) => {
    const root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    return `${root.replace(/\/?$/u, '/')}${String(path || '').replace(/^\/+/u, '')}`;
  };

  const toAbsoluteUrl = (value, base) => {
    const raw = String(value || '').trim();

    if (!raw || raw.length > maxDesignUrlLength) {
      return null;
    }

    try {
      return new URL(raw, base || window.location.origin);
    } catch (error) {
      return null;
    }
  };

  const assetContentType = (asset) => {
    const source = asset && typeof asset === 'object' ? asset : {};
    return String(
      source.contentType || source.content_type || source.mimeType || source.mime_type || '',
    )
      .split(';')[0]
      .trim()
      .toLowerCase();
  };

  const isAllowedUrlHost = (url, allowedHosts) =>
    url.protocol === 'https:' && allowedHosts.has(url.hostname.toLowerCase());

  const isCustomizerCompositePath = (url) =>
    url.hostname.toLowerCase() === 'customize.huinasports.com' &&
    /^\/api\/design\/[^/]+\/composite$/u.test(url.pathname);

  const hasImagePath = (url) => /\.(?:avif|gif|jpe?g|png|webp)$/iu.test(url.pathname);

  const hasCsvPath = (url) => /\.csv$/iu.test(url.pathname);

  const sanitizeDesignAssetUrl = (value, apiBase, asset, kind = 'image') => {
    const parsed = toAbsoluteUrl(value, apiBase);

    if (!parsed || !isAllowedUrlHost(parsed, allowedDesignAssetHosts)) {
      return '';
    }

    const contentType = assetContentType(asset);

    if (kind === 'csv') {
      if (contentType) {
        return allowedCsvContentTypes.has(contentType) ? parsed.href : '';
      }

      return hasCsvPath(parsed) ? parsed.href : '';
    }

    if (contentType) {
      return allowedImageContentTypes.has(contentType) ? parsed.href : '';
    }

    return hasImagePath(parsed) || isCustomizerCompositePath(parsed) ? parsed.href : '';
  };

  const sanitizeDesignLinkUrl = (value, base) => {
    const parsed = toAbsoluteUrl(value, base);

    if (!parsed || !isAllowedUrlHost(parsed, allowedDesignLinkHosts)) {
      return '';
    }

    return parsed.href;
  };

  const canonicalStyle = (value) => {
    const text = normalize(value);

    if (text.includes('reversible')) {
      return 'reversible';
    }

    if (text.includes('stitched') || text.includes('embroider') || text.includes('tackle twill')) {
      return 'stitched';
    }

    if (text.includes('printed') || text.includes('sublimat')) {
      return 'printed';
    }

    return text;
  };

  const canonicalPackage = (value) => {
    const text = normalize(value);

    if (text.includes('pant') || text.includes('shell')) {
      return 'jersey-socks-pant';
    }

    if (text.includes('sock')) {
      return 'jersey-socks';
    }

    if (text.includes('jersey')) {
      return 'jersey';
    }

    return text;
  };

  const canonicalTier = (value) => {
    const match = String(value || '').match(tierPattern);
    return match ? match[0] : normalize(value);
  };

  const variantHas = (variant, expected, canonicalize) =>
    Array.isArray(variant.options) &&
    variant.options.some((option) => canonicalize(option) === expected);

  const variantMatchesOptions = (variant, optionValues) =>
    optionValues.every((value, index) => variant.options[index] === value);

  const variantMatchesDesign = (variant, order) => {
    const style = canonicalStyle(order.style || order.craft);
    const packageName = canonicalPackage(order.package || order.packageName);
    const tier = canonicalTier(order.pricingTier || order.tier);

    return (
      variantHas(variant, style, canonicalStyle) &&
      variantHas(variant, packageName, canonicalPackage) &&
      variantHas(variant, tier, canonicalTier)
    );
  };

  const formatMoney = (cents, currencyCode) => {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  };

  const setText = (element, value) => {
    if (element) {
      element.textContent = value;
    }
  };

  const setProperty = (section, key, value) => {
    const input = Array.from(section.querySelectorAll('[data-design-property]')).find(
      (field) => field.dataset.designProperty === key,
    );

    if (!input) {
      return;
    }

    const cleanValue = value == null ? '' : String(value).trim();
    input.value = cleanValue;
    input.disabled = cleanValue === '';
  };

  const getNested = (source, path) =>
    path.reduce((current, key) => {
      if (!current || typeof current !== 'object') {
        return undefined;
      }

      return current[key];
    }, source);

  const summarizeRoster = (design) => {
    const rosterItems =
      design.rosterItems || design.roster || getNested(design, ['order', 'rosterItems']);

    if (Array.isArray(rosterItems)) {
      if (!rosterItems.length) {
        return '';
      }

      return `${rosterItems.length} roster item${rosterItems.length === 1 ? '' : 's'}`;
    }

    if (typeof rosterItems === 'string') {
      return rosterItems;
    }

    return '';
  };

  const getAssetUrl = (design, keys) => {
    for (const keyPath of keys) {
      const value = getNested(design, keyPath);

      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return '';
  };

  const normalizeAsset = (asset, fallbackUrl, apiBase, kind = 'image') => {
    const source = asset && typeof asset === 'object' ? asset : {};
    const url = sanitizeDesignAssetUrl(fallbackUrl || source.url || '', apiBase, source, kind);

    return {
      ...source,
      url,
    };
  };

  const normalizeDesignPayload = (payload, designIdFromUrl, apiBase) => {
    const source = payload && payload.design ? payload.design : payload || {};
    const fields = source.fields || (payload && payload.fields) || {};
    const order = source.order || {};
    const links = source.links || {};
    const rosterItems = source.rosterItems || source.roster || [];
    const style = order.style || fields.style || fields.order_style || fields.craft || '';
    const packageName =
      order.package || fields.package || fields.package_id || fields.packageName || '';
    const quantity = order.quantity || fields.quantity || fields.qty || '';
    const pricingTier = order.pricingTier || fields.pricing_tier || fields.pricingTier || '';
    const pricingVersion =
      order.pricingVersion || fields.pricing_version || fields.pricingVersion || '';
    const templateName =
      source.templateName || getNested(source, ['template', 'name']) || fields.template || '';
    const renderedUrl =
      getAssetUrl(source, [
        ['assets', 'renderedImage', 'url'],
        ['assets', 'renderedImageUrl'],
        ['assets', 'previewFront', 'url'],
      ]) ||
      source.composite_url ||
      (payload && payload.composite_url) ||
      '';
    const frontUrl = getAssetUrl(source, [
      ['assets', 'previewFront', 'url'],
      ['assets', 'previewFrontUrl'],
    ]);
    const backUrl = getAssetUrl(source, [
      ['assets', 'previewBack', 'url'],
      ['assets', 'previewBackUrl'],
    ]);
    const rosterCsvUrl = getAssetUrl(source, [
      ['assets', 'rosterCsv', 'url'],
      ['assets', 'rosterCsvUrl'],
      ['assets', 'rosterCSVUrl'],
    ]);

    const renderedImageAsset = normalizeAsset(
      getNested(source, ['assets', 'renderedImage']),
      renderedUrl,
      apiBase,
      'image',
    );
    const previewFrontAsset = normalizeAsset(
      getNested(source, ['assets', 'previewFront']),
      frontUrl || renderedUrl,
      apiBase,
      'image',
    );
    const previewBackAsset = normalizeAsset(
      getNested(source, ['assets', 'previewBack']),
      backUrl,
      apiBase,
      'image',
    );
    const rosterCsvAsset = normalizeAsset(
      getNested(source, ['assets', 'rosterCsv']),
      rosterCsvUrl,
      apiBase,
      'csv',
    );
    const centerLogoAsset = normalizeAsset(
      getNested(source, ['logos', 'center']),
      fields.logo_center_url,
      apiBase,
      'image',
    );
    const leftShoulderLogoAsset = normalizeAsset(
      getNested(source, ['logos', 'leftShoulder']),
      fields.logo_left_url,
      apiBase,
      'image',
    );
    const rightShoulderLogoAsset = normalizeAsset(
      getNested(source, ['logos', 'rightShoulder']),
      fields.logo_right_url,
      apiBase,
      'image',
    );

    return {
      ...source,
      id: source.id || source.design_id || (payload && payload.design_id) || designIdFromUrl,
      teamName: source.teamName || source.team || fields.team_name || fields.teamName || '',
      templateName,
      rosterItems,
      notes: source.productionNotes || order.productionNotes || '',
      order: {
        ...order,
        style,
        package: packageName,
        quantity,
        pricingTier,
        pricingVersion,
      },
      links: {
        ...links,
        designUrl: sanitizeDesignLinkUrl(
          source.designUrl ||
            links.designUrl ||
            source.share_url ||
            (payload && payload.share_url) ||
            '',
          apiBase,
        ),
      },
      assets: {
        ...source.assets,
        renderedImage: renderedImageAsset,
        renderedImageUrl: renderedImageAsset.url,
        previewFront: previewFrontAsset,
        previewFrontUrl: previewFrontAsset.url,
        previewBack: previewBackAsset,
        previewBackUrl: previewBackAsset.url,
        rosterCsv: rosterCsvAsset,
        rosterCsvUrl: rosterCsvAsset.url,
        rosterCSVUrl: rosterCsvAsset.url,
      },
      logos: {
        ...source.logos,
        center: centerLogoAsset,
        leftShoulder: leftShoulderLogoAsset,
        rightShoulder: rightShoulderLogoAsset,
      },
    };
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = Number(count) <= 0;
    });
  };

  sections.forEach((section) => {
    const form = section.querySelector('[data-product-form]');
    const variantJson = section.querySelector('[data-product-variants-json]');
    const variantIdInput = section.querySelector('[data-variant-id]');
    const optionSelectors = Array.from(section.querySelectorAll('[data-product-option]'));
    const priceElement = section.querySelector('[data-product-price]');
    const addButton = section.querySelector('[data-add-button]');
    const statusElement = section.querySelector('[data-product-status]');
    const quantityInput = section.querySelector('[data-quantity-input]');
    const bridgePanel = section.querySelector('[data-design-bridge]');
    const bridgeSummary = section.querySelector('[data-design-summary]');
    const currencyCode = section.dataset.currencyCode || 'USD';
    let variants = [];
    let designBridgeActive = false;

    try {
      variants = JSON.parse(variantJson ? variantJson.textContent : '[]');
    } catch (error) {
      variants = [];
    }

    const setStatus = (key) => {
      const message = section.dataset[key] || '';
      setText(statusElement, message);
      return message;
    };

    const setButtonDisabled = (disabled) => {
      if (addButton) {
        addButton.disabled = disabled;
      }
    };

    const updateVariant = (variant) => {
      if (!variant) {
        setButtonDisabled(true);
        setStatus('messageUnavailable');
        return;
      }

      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }

      setText(priceElement, formatMoney(variant.price, currencyCode));
      setButtonDisabled(!variant.available);
      setStatus(variant.available ? 'messageReady' : 'messageUnavailable');
    };

    const findSelectedVariant = () => {
      const selectedValues = optionSelectors.map((selector) => selector.value);

      if (!selectedValues.length) {
        return variants[0];
      }

      return variants.find((variant) => variantMatchesOptions(variant, selectedValues));
    };

    optionSelectors.forEach((selector) => {
      selector.addEventListener('change', () => {
        if (designBridgeActive) {
          return;
        }

        updateVariant(findSelectedVariant());
      });
    });

    const applyVariantToSelectors = (variant) => {
      optionSelectors.forEach((selector, index) => {
        selector.value = variant.options[index] || selector.value;
        selector.disabled = true;
      });
    };

    const applyDesign = (payload, designIdFromUrl) => {
      const apiBase = String(section.dataset.customizerApiUrl || '').replace(/\/$/u, '');
      const design = normalizeDesignPayload(payload, designIdFromUrl, apiBase);
      const order = design.order || {};
      const designId = normalizeDesignId(design.id || designIdFromUrl);
      const teamName = design.teamName || design.team || '';
      const templateName = design.templateName || '';
      const quantity = Number(order.quantity || design.quantity || 0);
      const designUrl = design.designUrl || getNested(design, ['links', 'designUrl']) || '';
      const rosterSummary = summarizeRoster(design);
      const rosterCsvUrl = getAssetUrl(design, [
        ['assets', 'rosterCsv', 'url'],
        ['assets', 'rosterCsvUrl'],
        ['assets', 'rosterCSVUrl'],
      ]);
      const renderedUrl = getAssetUrl(design, [
        ['assets', 'renderedImage', 'url'],
        ['assets', 'renderedImageUrl'],
        ['assets', 'previewFront', 'url'],
      ]);
      const frontUrl = getAssetUrl(design, [
        ['assets', 'previewFront', 'url'],
        ['assets', 'previewFrontUrl'],
      ]);
      const backUrl = getAssetUrl(design, [
        ['assets', 'previewBack', 'url'],
        ['assets', 'previewBackUrl'],
      ]);
      const centerLogoUrl = getAssetUrl(design, [['logos', 'center', 'url']]);
      const leftShoulderLogoUrl = getAssetUrl(design, [['logos', 'leftShoulder', 'url']]);
      const rightShoulderLogoUrl = getAssetUrl(design, [['logos', 'rightShoulder', 'url']]);
      const matchingVariant = variants.find((variant) => variantMatchesDesign(variant, order));

      designBridgeActive = true;

      if (bridgePanel) {
        bridgePanel.hidden = false;
      }

      setProperty(section, 'design_id', designId);
      setProperty(section, 'roster', rosterSummary);
      setProperty(section, 'package', order.package || '');
      setProperty(section, 'craft', order.style || order.craft || '');
      setProperty(section, 'proof', renderedUrl);
      setProperty(section, '_Design Link', designUrl);
      setProperty(section, 'Design ID', designId);
      setProperty(section, 'Team Name', teamName);
      setProperty(section, 'Template', templateName);
      setProperty(section, 'Style', order.style || order.craft || '');
      setProperty(section, 'Package', order.package || '');
      setProperty(section, 'Quantity Tier', order.pricingTier || '');
      setProperty(section, 'Pricing Version', order.pricingVersion || '');
      setProperty(section, 'Roster Summary', rosterSummary);
      setProperty(section, 'Roster CSV URL', rosterCsvUrl);
      setProperty(section, 'Rendered Preview URL', renderedUrl);
      setProperty(section, 'Front Preview URL', frontUrl);
      setProperty(section, 'Back Preview URL', backUrl);
      setProperty(section, 'Center Logo URL', centerLogoUrl);
      setProperty(section, 'Left Shoulder Logo URL', leftShoulderLogoUrl);
      setProperty(section, 'Right Shoulder Logo URL', rightShoulderLogoUrl);
      setProperty(section, 'Production Notes', design.notes || '');

      if (quantity > 0 && quantityInput) {
        quantityInput.value = String(quantity);
        quantityInput.min = String(quantity);
        quantityInput.readOnly = true;
      }

      if (!matchingVariant) {
        setButtonDisabled(true);
        const message = setStatus('messageVariantMissing');
        setText(bridgeSummary, message);
        return;
      }

      applyVariantToSelectors(matchingVariant);
      updateVariant(matchingVariant);
      const loadedMessage = section.dataset.messageDesignLoaded || '';
      setText(bridgeSummary, loadedMessage);
    };

    const loadDesignFromUrl = async () => {
      const params = new URLSearchParams(window.location.search);
      const designId = normalizeDesignId(params.get('design_id'));

      if (!designId) {
        return;
      }

      if (!isValidDesignId(designId)) {
        setButtonDisabled(true);
        const message = setStatus('messageInvalidDesign');

        if (bridgePanel) {
          bridgePanel.hidden = false;
        }

        setText(bridgeSummary, message);
        return;
      }

      setButtonDisabled(true);
      setStatus('messageLoadingDesign');

      if (bridgePanel) {
        bridgePanel.hidden = false;
      }

      setText(bridgeSummary, section.dataset.messageLoadingDesign || '');

      try {
        const apiBase = String(section.dataset.customizerApiUrl || '').replace(/\/$/u, '');
        const response = await fetch(`${apiBase}/${encodeURIComponent(designId)}`, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`Design API returned ${response.status}`);
        }

        applyDesign(await response.json(), designId);
      } catch (error) {
        setButtonDisabled(true);
        const message = setStatus('messageDesignUnavailable');
        setText(bridgeSummary, message);
      }
    };

    if (form) {
      form.addEventListener('submit', async (event) => {
        if (!window.fetch || !window.FormData) {
          return;
        }

        event.preventDefault();
        setButtonDisabled(true);
        setStatus('messageAdding');

        try {
          const response = await fetch(shopifyRoute('cart/add.js'), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });

          if (!response.ok) {
            throw new Error(`Cart add returned ${response.status}`);
          }

          const cartResponse = await fetch(shopifyRoute('cart.js'), {
            headers: { Accept: 'application/json' },
          });

          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            updateCartCount(cart.item_count);
          }

          setStatus('messageAdded');
        } catch (error) {
          setStatus('messageAddError');
        } finally {
          const currentVariant = variants.find(
            (variant) => String(variant.id) === String(variantIdInput ? variantIdInput.value : ''),
          );

          setButtonDisabled(!currentVariant || !currentVariant.available);
        }
      });
    }

    updateVariant(findSelectedVariant());
    loadDesignFromUrl();
  });
})();
